import axios from 'axios';

const SQUARE_PAYMENTS_URL = process.env.REACT_APP_SQUARE_PAYMENTS_URL || 'http://localhost:8000';

/**
 * Create a checkout on the Square Terminal
 * @param {Object} params
 * @param {string} params.deviceId - Square Terminal device ID
 * @param {string} params.locationId - Square location ID
 * @param {number} params.amountCents - Amount in cents
 * @param {string} params.referenceId - External reference (e.g., ticket ID)
 * @param {string} params.note - Note to display on terminal
 * @returns {Promise<Object>} Checkout response
 */
export const createCheckout = async ({ deviceId, locationId, amountCents, referenceId, note }) => {
    const response = await axios.post(`${SQUARE_PAYMENTS_URL}/checkouts`, {
        device_id: deviceId,
        location_id: locationId,
        amount_cents: amountCents,
        currency: 'USD',
        reference_id: referenceId,
        note: note,
        tip_settings: {
            allow_tipping: true,
            tip_percentages: [15, 20, 25],
            custom_tip_field: true,
            separate_tip_screen: true,
        },
    }, {
        timeout: 30000,
    });
    return response.data;
};

/**
 * Get checkout status
 * @param {string} checkoutId - Square checkout ID
 * @returns {Promise<Object>} Checkout status
 */
export const getCheckoutStatus = async (checkoutId) => {
    const response = await axios.get(`${SQUARE_PAYMENTS_URL}/checkouts/${checkoutId}`, {
        timeout: 10000,
    });
    return response.data;
};

/**
 * Wait for checkout to complete (uses server-side long polling)
 * @param {string} checkoutId - Square checkout ID
 * @param {number} timeoutMs - Max wait time in ms
 * @returns {Promise<Object>} Final checkout status
 */
export const waitForCheckout = async (checkoutId, timeoutMs = 120000) => {
    const response = await axios.get(`${SQUARE_PAYMENTS_URL}/checkouts/${checkoutId}/wait`, {
        params: { timeout_ms: timeoutMs },
        timeout: timeoutMs + 10000, // Add buffer for network
    });
    return response.data;
};

/**
 * Cancel a pending checkout
 * @param {string} checkoutId - Square checkout ID
 * @returns {Promise<Object>} Cancelled checkout
 */
export const cancelCheckout = async (checkoutId) => {
    const response = await axios.post(`${SQUARE_PAYMENTS_URL}/checkouts/${checkoutId}/cancel`, {}, {
        timeout: 10000,
    });
    return response.data;
};

/**
 * Get list of devices for a location
 * @param {string} locationId - Square location ID
 * @returns {Promise<Array>} List of devices
 */
export const getDevices = async (locationId) => {
    const response = await axios.get(`${SQUARE_PAYMENTS_URL}/devices`, {
        params: { location_id: locationId },
        timeout: 10000,
    });
    return response.data;
};

/**
 * Terminal checkout statuses
 */
export const CHECKOUT_STATUS = {
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    CANCELED: 'CANCELED',
    FAILED: 'FAILED',
};

/**
 * Check if status is terminal (final)
 */
export const isTerminalStatus = (status) => {
    return [CHECKOUT_STATUS.COMPLETED, CHECKOUT_STATUS.CANCELED, CHECKOUT_STATUS.FAILED].includes(status);
};

/**
 * Check if the Square payments server is healthy
 * @returns {Promise<{healthy: boolean, error?: string}>}
 */
export const checkHealth = async () => {
    try {
        const response = await axios.get(`${SQUARE_PAYMENTS_URL}/health`, {
            timeout: 5000,
        });
        // Server returns square_env, secrets_loaded, square_client_ready
        const data = response.data;
        const healthy = data && data.secrets_loaded && data.square_client_ready;
        return { healthy };
    } catch (err) {
        return {
            healthy: false,
            error: err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK'
                ? 'Square payments server is not running'
                : err.message,
        };
    }
};

/**
 * Location ID mapping (DaySmart location ID -> Square location ID)
 */
export const SQUARE_LOCATION_IDS = {
    1: 'LJTW730WYK8YH', // Hopkins
    2: 'LJ1MQ37QYNW42', // Inver Grove
};

/**
 * Device IDs by location (hardcoded for now, could be fetched dynamically)
 */
export const SQUARE_DEVICE_IDS = {
    1: '541CS149C3005265', // Hopkins terminal
    // 2: 'DEVICE_ID_FOR_INVER_GROVE', // Add when paired
};
